import React, { useRef, useState } from "react";
import { Plus, Trash2, Upload } from "lucide-react";

import { uploadFile } from "@/api/uploads";
import { Button } from "@/components/primitives/Button";
import { Input } from "@/components/primitives/Input";
import { TextArea } from "@/components/primitives/TextArea";
import type { MultipartConfig, MultipartFileField } from "@/types";

interface MultipartStepFieldsProps {
  config: MultipartConfig | null;
  onChange: (config: MultipartConfig | null) => void;
}

const emptyConfig = (): MultipartConfig => ({ form_fields: {}, file_fields: {} });

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <h5 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
    {children}
  </h5>
);

export const MultipartStepFields: React.FC<MultipartStepFieldsProps> = ({ config, onChange }) => {
  const cfg = config ?? emptyConfig();
  const [uploadingField, setUploadingField] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputs = useRef<Record<string, HTMLInputElement | null>>({});

  const updateConfig = (patch: Partial<MultipartConfig>) => {
    onChange({ ...cfg, ...patch });
  };

  // ----- text form fields -----
  const formEntries = Object.entries(cfg.form_fields);

  const setFormEntry = (index: number, key: string, value: string) => {
    const entries = formEntries.map(([k, v], i) => (i === index ? [key, v] : [k, v]));
    // Value edits keep the key; key edits rebuild the dict.
    const next: Record<string, string> = {};
    entries.forEach(([k, v], i) => {
      next[i === index ? key : k] = i === index ? value : v;
    });
    updateConfig({ form_fields: next });
  };

  const addFormEntry = () => {
    const entries = Object.fromEntries([...formEntries, ["", ""]]);
    updateConfig({ form_fields: entries });
  };

  const removeFormEntry = (index: number) => {
    const entries = formEntries.filter((_, i) => i !== index);
    updateConfig({ form_fields: Object.fromEntries(entries) });
  };

  // ----- file fields -----
  const fileEntries = Object.entries(cfg.file_fields);

  const setFileFieldName = (index: number, name: string) => {
    const entries = fileEntries.map(([k, v], i) => (i === index ? [name, v] : [k, v]));
    updateConfig({ file_fields: Object.fromEntries(entries) });
  };

  const addFileField = () => {
    updateConfig({
      file_fields: {
        ...cfg.file_fields,
        "": { stored_path: "", filename: "", content_type: null } as MultipartFileField,
      },
    });
  };

  const removeFileField = (index: number) => {
    const entries = fileEntries.filter((_, i) => i !== index);
    updateConfig({ file_fields: Object.fromEntries(entries) });
  };

  const handleFileSelected = async (fieldName: string, file: File | undefined) => {
    if (!file) return;
    setError(null);
    setUploadingField(fieldName);
    try {
      const meta = await uploadFile(file);
      updateConfig({
        file_fields: { ...cfg.file_fields, [fieldName]: meta },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "File upload failed");
    } finally {
      setUploadingField(null);
    }
  };

  return (
    <div className="flex flex-col gap-5 rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
      <div className="flex flex-col gap-3">
        <SectionTitle>Form fields (text)</SectionTitle>
        {formEntries.length === 0 && (
          <p className="text-sm text-slate-500 dark:text-slate-400">
            No form fields yet. Add fields such as <code>agent</code> for the analyse endpoint.
          </p>
        )}
        {formEntries.map(([key, value], index) => (
          <div key={index} className="grid grid-cols-1 items-start gap-2 sm:grid-cols-[10rem,minmax(0,1fr),auto]">
            <div className="min-w-0">
              <Input
                label={index === 0 ? "Field name" : undefined}
                value={key}
                onChange={(e) => setFormEntry(index, e.target.value, value)}
                placeholder="agent"
              />
            </div>
            <div className="min-w-0">
              <TextArea
                label={index === 0 ? "Value" : undefined}
                value={value}
                onChange={(e) => setFormEntry(index, key, e.target.value)}
                placeholder='{"instructions": [...], "rules": [...], "examples": ...}'
                rows={4}
              />
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => removeFormEntry(index)}
              className="mt-1 text-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-900/30"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <div>
          <Button type="button" variant="secondary" size="sm" onClick={addFormEntry} className="gap-1">
            <Plus className="h-4 w-4" /> Add form field
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-3 border-t border-slate-100 pt-4 dark:border-slate-800">
        <SectionTitle>File fields</SectionTitle>
        {fileEntries.length === 0 && (
          <p className="text-sm text-slate-500 dark:text-slate-400">
            No file fields yet. Add a field such as <code>file</code> and upload an audio file.
          </p>
        )}
        {fileEntries.map(([name, meta], index) => (
          <div key={index} className="grid grid-cols-1 items-end gap-2 sm:grid-cols-[10rem,minmax(0,1fr),auto]">
            <div className="min-w-0">
              <Input
                label={index === 0 ? "Field name" : undefined}
                value={name}
                onChange={(e) => setFileFieldName(index, e.target.value)}
                placeholder="file"
              />
            </div>
            <div className="min-w-0">
              {index === 0 && (
                <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
                  File
                </label>
              )}
              <div className="flex min-w-0 items-center gap-2">
                <input
                  ref={(el) => {
                    fileInputs.current[name] = el;
                  }}
                  type="file"
                  className="hidden"
                  onChange={(e) => handleFileSelected(name, e.target.files?.[0])}
                />
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={!name || uploadingField === name}
                  onClick={() => fileInputs.current[name]?.click()}
                  className="gap-1"
                >
                  <Upload className="h-4 w-4" />
                  {uploadingField === name ? "Uploading…" : meta.filename ? "Replace" : "Choose file"}
                </Button>
                {meta.filename && (
                  <span
                    className="truncate text-sm text-slate-600 dark:text-slate-300"
                    title={meta.filename}
                  >
                    {meta.filename}
                  </span>
                )}
              </div>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => removeFileField(index)}
              className="text-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-900/30"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
        {error && (
          <p className="text-sm text-red-600 dark:text-red-400" role="alert">
            {error}
          </p>
        )}
        <div>
          <Button type="button" variant="secondary" size="sm" onClick={addFileField} className="gap-1">
            <Plus className="h-4 w-4" /> Add file field
          </Button>
        </div>
      </div>
    </div>
  );
};
